// src/components/SubmitTime/components/TimeInputFields.tsx
// Sub-component for time-related input fields

import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import type { ValidationState } from '../types';

interface DateInputProps {
  value?: string;
  onClick?: () => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  className?: string;
  placeholder?: string;
}

const DateInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ value, onClick, onChange, className, placeholder }, ref) => (
    <div className="submit-time-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        onChange={onChange || (() => {})}
        className={className}
        placeholder={placeholder}
        readOnly
      />
      <button type="button" className="submit-time-date-icon-btn" onClick={onClick} tabIndex={-1}>
        <CalendarTodayIcon style={{ fontSize: 14 }} />
      </button>
    </div>
  ),
);
DateInput.displayName = 'DateInput';

interface TimeInputFieldsProps {
  hours: string;
  date: string;
  estimatedField: string;
  remainingField: string;
  actualField: string;
  description: string;
  valForm: ValidationState;
  onHoursChange: (value: string) => void;
  onHoursBlur?: () => void;
  hoursFormat?: boolean;
  onDateChange: (value: string) => void;
  onEstimatedChange: (value: string) => void;
  onRemainingChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  showEstimated?: boolean;
  showRemaining?: boolean;
  showActual?: boolean;
  showDescription?: boolean;
  estimatedEditable?: boolean;
  remainingEditable?: boolean;
  actualEditable?: boolean;
  timeTrackingTeam?: boolean;
  customKey?: number;
}

export const TimeInputFields: React.FC<TimeInputFieldsProps> = ({
  hours,
  date,
  estimatedField,
  remainingField,
  actualField,
  description,
  valForm,
  onHoursChange,
  onHoursBlur,
  hoursFormat = false,
  onDateChange,
  onEstimatedChange,
  onRemainingChange,
  onDescriptionChange,
  showEstimated = true,
  showRemaining = true,
  showActual = false,
  showDescription = false,
  estimatedEditable = false,
  remainingEditable = true,
  actualEditable: _actualEditable = false,
  timeTrackingTeam = false,
  customKey = 0,
}) =>
{
  return (
    <div className="time-input-fields">
      {/* Estimated */}
      {showEstimated && (
        <div className="corgee-layout-tableSubmitTimet">
          <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
            <span>Estimated:</span>
          </div>
          <div className="corgee-layout-table-cellSubmitTimet">
            <input
              type="text"
              className={`corgee-inpuSubmitTimet input-disable ${valForm.estimated ? 'focusInput' : ''}`}
              value={estimatedField}
              onChange={e => onEstimatedChange(e.target.value)}
              disabled={!estimatedEditable}
              data-tooltip-id={`EstimatedTooltip${customKey}`}
            />
            {valForm.estimated && (
              <span className="submit-required-field" style={{ fontSize: '11px' }}>
                Estimated is required. Check that the task or phase has the selected layer.
              </span>
            )}
          </div>
        </div>
      )}

      {/* Date */}
      <div className="corgee-layout-tableSubmitTimet">
        <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
          <span>Date:</span>
        </div>
        <div className="corgee-layout-table-cellSubmitTimet">
          <DatePicker
            selected={date ? new Date(date + 'T00:00:00') : null}
            onChange={(selectedDate: Date | null) =>
            {
              if (!selectedDate)
              {
                onDateChange('');
              }
              else
              {
                const y = selectedDate.getFullYear();
                const m = String(selectedDate.getMonth() + 1).padStart(2, '0');
                const d = String(selectedDate.getDate()).padStart(2, '0');
                onDateChange(`${y}-${m}-${d}`);
              }
            }}
            dateFormat="MM/dd/yyyy"
            placeholderText="MM/DD/YYYY"
            className={`corgee-inpuSubmitTimet${valForm.date ? ' focusInput' : ''}`}
            wrapperClassName="submit-time-datepicker-wrapper"
            customInput={<DateInput />}
            popperProps={{ strategy: 'fixed' }}
            showPopperArrow={false}
          />
        </div>
      </div>

      {/* Hours */}
      <div className="corgee-layout-tableSubmitTimet">
        <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
          <span>Hours:</span>
        </div>
        <div className="corgee-layout-table-cellSubmitTimet">
          <input
            type="text"
            className={`corgee-inpuSubmitTimet ${valForm.hours ? 'focusInput' : ''}`}
            value={hours}
            onChange={e => onHoursChange(e.target.value)}
            onBlur={onHoursBlur}
            placeholder="Hours"
            data-tooltip-id={`HoursTooltip${customKey}`}
          />
          {hoursFormat && (
            <span className="tooltip-warning-hours">
              * Hours must always be in the format of time in hours.
            </span>
          )}
          {valForm.hours && !hoursFormat && (hours === '0:00' || hours === '00:00') && (
            <span className="tooltip-warning-hours">Hours is required and cannot be 0:00</span>
          )}
          {valForm.hoursOutOfRange && (
            <span className="tooltip-warning-hours">
              {timeTrackingTeam
                ? 'You cannot enter 250 or more hours.'
                : 'You cannot enter 24 or more hours.'}
            </span>
          )}
          {valForm.maxHours && (
            <span className="tooltip-warning-hours">
              Maximum hours per day exceeded ({timeTrackingTeam ? '250' : '16'} hours limit)
            </span>
          )}
        </div>
      </div>

      {/* Remaining */}
      {showRemaining && (
        <div className="corgee-layout-tableSubmitTimet">
          <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
            <span>Remaining:</span>
          </div>
          <div className="corgee-layout-table-cellSubmitTimet">
            <input
              type="text"
              className="corgee-inpuSubmitTimet"
              value={remainingField}
              onChange={e => onRemainingChange(e.target.value)}
              disabled={!remainingEditable}
              data-tooltip-id={`RemainingTooltip${customKey}`}
            />
          </div>
        </div>
      )}

      {/* Actual */}
      {showActual && (
        <div className="corgee-layout-tableSubmitTimet">
          <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
            <span>Actual:</span>
          </div>
          <div className="corgee-layout-table-cellSubmitTimet">
            <input
              type="text"
              className="corgee-inpuSubmitTimet input-disable"
              value={actualField}
              disabled={true}
            />
          </div>
        </div>
      )}

      {/* Description (hidden by default) */}
      {showDescription && (
        <div className="corgee-layout-tableSubmitTimet">
          <div className="corgee-layout-table-cellSubmitTimet layout-width-20SubmitTime">
            <span>Description:</span>
          </div>
          <div className="corgee-layout-table-cellSubmitTimet">
            <input
              type="text"
              className="corgee-inpuSubmitTimet"
              value={description}
              onChange={e => onDescriptionChange(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeInputFields;
